// ---------------------------------------------------------
//  Messages Screen -- Chat / Messaging with sub-tabs
// ---------------------------------------------------------

import { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { t } from "@/lib/i18n";
import {
  ArrowLeft, Send, Users, MessageCircle, UserCircle,
  Hash, Clock,
} from "lucide-react";

interface Channel {
  channelType: string;
  channelId: string;
  name: string;
}

interface Message {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  createdAt: string;
  isMe: boolean;
}

interface MessagesScreenProps {
  channels: Channel[];
  onSelectChannel: (channelType: string, channelId: string) => void;
  activeChannel: { channelType: string; channelId: string } | null;
  messages: Message[];
  onSendMessage: (text: string) => void;
  onBack: () => void;
}

const TAB_CONFIG = [
  { id: "halka", label: "Halka", icon: Users },
  { id: "shura", label: "Istisare", icon: MessageCircle },
  { id: "dm", label: "Arkadaslar", icon: UserCircle },
] as const;

function getChannelIcon(type: string) {
  switch (type) {
    case "halka": return Users;
    case "shura": return MessageCircle;
    case "dm": return UserCircle;
    default: return Hash;
  }
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export function MessagesScreen({
  channels, onSelectChannel, activeChannel, messages, onSendMessage, onBack,
}: MessagesScreenProps) {
  const [tab, setTab] = useState<string>("halka");
  const [draft, setDraft] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const filteredChannels = channels.filter(c => c.channelType === tab);

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;
    onSendMessage(text);
    setDraft("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // -- Chat View (active channel selected) --
  if (activeChannel) {
    const channelName = channels.find(
      c => c.channelId === activeChannel.channelId && c.channelType === activeChannel.channelType,
    )?.name ?? "Sohbet";

    const ChannelIcon = getChannelIcon(activeChannel.channelType);

    return (
      <div className="flex flex-col h-[calc(100vh-5rem)]">
        {/* Chat header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card/80 backdrop-blur-sm shrink-0">
          <button
            onClick={onBack}
            className="w-8 h-8 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
            aria-label={t("back")}
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-9 h-9 rounded-full bg-gold/10 flex items-center justify-center">
            <ChannelIcon size={18} className="text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold truncate">{channelName}</h2>
            <p className="text-[10px] text-muted-foreground">
              {activeChannel.channelType === "dm" ? "Ozel Mesaj" : "Grup Sohbeti"}
            </p>
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-3">
                <MessageCircle size={24} className="text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">Henuz mesaj yok</p>
              <p className="text-[10px] text-muted-foreground mt-1">Ilk mesaji gonderin!</p>
            </div>
          )}

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                "flex flex-col max-w-[80%]",
                msg.isMe ? "ml-auto items-end" : "mr-auto items-start",
              )}
            >
              {/* Sender name (for group chats, not own messages) */}
              {!msg.isMe && activeChannel.channelType !== "dm" && (
                <span className="text-[10px] font-semibold text-gold/70 mb-0.5 px-1">
                  {msg.senderName}
                </span>
              )}

              <div
                className={cn(
                  "rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                  msg.isMe
                    ? "bg-gold text-ehille-dark rounded-br-md"
                    : "bg-muted rounded-bl-md",
                )}
              >
                {msg.isMe && (
                  <Badge variant="warning" className="text-[8px] h-3.5 px-1 mb-1 bg-ehille-dark/20 text-ehille-dark border-0">
                    Sen
                  </Badge>
                )}
                <p>{msg.text}</p>
              </div>

              {/* Timestamp */}
              <div className="flex items-center gap-1 mt-0.5 px-1">
                <Clock size={9} className="text-muted-foreground" />
                <span className="text-[9px] text-muted-foreground">{formatTime(msg.createdAt)}</span>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <div className="shrink-0 px-4 py-3 border-t border-border bg-card/80 backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <Input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Mesajinizi yazin..."
              className="flex-1 h-11 rounded-xl"
              aria-label="Mesaj girdisi"
            />
            <Button
              onClick={handleSend}
              disabled={!draft.trim()}
              size="icon"
              className="w-11 h-11 rounded-xl bg-gold hover:bg-gold/90 text-ehille-dark shrink-0"
              aria-label="Mesaj gonder"
            >
              <Send size={18} />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // -- Channel List View --
  return (
    <div className="pb-24 px-4 pt-6">
      {/* Header */}
      <h1 className="text-xl font-bold mb-6 flex items-center gap-2">
        <MessageCircle size={22} className="text-gold" />
        Mesajlar
      </h1>

      {/* Sub-tabs */}
      <div className="flex bg-muted rounded-xl p-1 mb-6">
        {TAB_CONFIG.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={cn(
              "flex-1 py-2 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5",
              tab === id
                ? "bg-card text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Channel list */}
      {filteredChannels.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
            {tab === "halka" && <Users size={24} className="text-muted-foreground" />}
            {tab === "shura" && <MessageCircle size={24} className="text-muted-foreground" />}
            {tab === "dm" && <UserCircle size={24} className="text-muted-foreground" />}
          </div>
          <p className="text-sm text-muted-foreground">
            {tab === "halka" && "Halka sohbeti bulunamadi"}
            {tab === "shura" && "Istisare kanali bulunamadi"}
            {tab === "dm" && "Ozel mesaj bulunamadi"}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredChannels.map((channel) => {
            const Icon = getChannelIcon(channel.channelType);
            return (
              <Card
                key={`${channel.channelType}-${channel.channelId}`}
                className="cursor-pointer hover:border-gold/50 transition-all active:scale-[0.98]"
                onClick={() => onSelectChannel(channel.channelType, channel.channelId)}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-gold" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{channel.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {channel.channelType === "halka" && "Halka Sohbeti"}
                      {channel.channelType === "shura" && "Istisare"}
                      {channel.channelType === "dm" && "Ozel Mesaj"}
                    </p>
                  </div>
                  <ArrowLeft size={16} className="text-muted-foreground rotate-180 shrink-0" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
